import { Alert, Button, Container, Group, Stack, Tabs, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Practitioner } from '@medplum/fhirtypes';
import { IconCheck, IconClipboardList, IconPlus, IconTransfer, IconUser, IconUsers } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useClients } from '../../contexts/ClientsContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { fetchAdminDirectory } from '../../lib/adminDirectoryService';
import { ADMIN_EXTENSION_URLS } from '../../lib/adminDirectoryConstants';
import { organizationIdFromPractitioner } from '../../lib/createFulfilledEpisodePair';
import { canRoleAddClient } from '../../lib/staffAddClientPermissions';
import { StaffAddClientModal } from '../users/components/StaffAddClientModal';
import { STAFF_ROLES, type Practice } from '../users/types/rbac';
import { FEATURE_VISIBILITY } from '../../config/featureVisibility';
import { LocalhostOnlyBadge } from '../../components/LocalhostOnlyBadge';
import { AllTab } from './tabs/AllTab';
import { ClientsTab } from './tabs/ClientsTab';
import { CaseloadContactsPage } from './CaseloadContactsPage';
import { ReferralsTab } from './tabs/ReferralsTab';
import { TransfersTab } from './tabs/TransfersTab';
import { resolveCaseloadActiveTab } from './caseloadTabs';
import type { DischargeSummary } from '../clients/data';

function profilePracticeId(profile: ReturnType<typeof useMedplumProfile>): string | undefined {
  if (profile?.resourceType !== 'Practitioner' || !profile.id) return undefined;
  const pract = profile as Practitioner;
  const fromExt = organizationIdFromPractitioner(pract);
  if (fromExt) return fromExt;
  const extOrg = pract.extension?.find((e) => e.url === ADMIN_EXTENSION_URLS.organization)
    ?.valueReference?.reference;
  return extOrg?.startsWith('Organization/') ? extOrg.replace('Organization/', '') : undefined;
}

function isPlatformStaffRole(role: (typeof STAFF_ROLES)[number]): boolean {
  return role === 'unify-admin' || role === 'unify-assistant';
}

/**
 * Caseload management screen
 * Displays client caseload with various status tabs
 */
export function ClientsScreen(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const location = useLocation();
  const { role } = usePermissions();
  const { clients, setClients, clientGroups, addSessionDischargeSummary, caseloadError, caseloadLoading, reloadCaseload } = useClients();
  const [searchParams, setSearchParams] = useSearchParams();
  const caseloadTabVisibility = {
    allTab: FEATURE_VISIBILITY.caseloadAllTab,
    referralRequests: FEATURE_VISIBILITY.referralRequests,
    transferPortal: FEATURE_VISIBILITY.caseloadTransferPortal,
  };
  const activeTab = resolveCaseloadActiveTab(searchParams.get('tab'), caseloadTabVisibility);
  const [tabDataRefreshKey, setTabDataRefreshKey] = useState(0);
  const canAddClient = canRoleAddClient(role);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [scopePractice, setScopePractice] = useState<Practice | undefined>();
  const [addClientLoading, setAddClientLoading] = useState(false);

  const handleDischarge = useCallback((_clientId: string, summary: DischargeSummary) => {
    addSessionDischargeSummary(summary);
  }, [addSessionDischargeSummary]);

  useEffect(() => {
    reloadCaseload();
    setTabDataRefreshKey((key) => key + 1);
  }, [location.pathname, activeTab, reloadCaseload]);

  const handleOpenAddClient = useCallback(async () => {
    setAddClientLoading(true);
    try {
      const directory = await fetchAdminDirectory(medplum);
      setPractices(directory.practices);
      const orgId = profilePracticeId(profile);
      if (!isPlatformStaffRole(role) && orgId) {
        setScopePractice(directory.practices.find((p) => p.id === orgId));
      } else {
        setScopePractice(undefined);
      }
      setShowAddClientModal(true);
    } catch (err) {
      console.error('[Caseload] load practices for add client failed:', err);
      notifications.show({
        color: 'red',
        title: 'Could not open add client',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setAddClientLoading(false);
    }
  }, [medplum, profile, role]);

  const practicesForModal = scopePractice != null ? [scopePractice] : practices;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <div>
            <Group gap="sm" align="center" wrap="wrap">
              <Title order={2}>Caseload</Title>
              {(activeTab === 'all' || activeTab === 'referral-requests' || activeTab === 'transfers') ? (
                <LocalhostOnlyBadge />
              ) : null}
            </Group>
            <Text c="dimmed" size="sm">
              Manage your client caseload and track client status
            </Text>
          </div>
          {canAddClient ? (
            <Button
              leftSection={<IconPlus size={16} />}
              loading={addClientLoading}
              onClick={() => void handleOpenAddClient()}
            >
              Add Client
            </Button>
          ) : null}
        </Group>

        {caseloadError && (
          <Alert color="red" title="Could not load caseload from Medplum">
            {caseloadError.message}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(value) => {
            if (!value || value === 'clients') {
              setSearchParams({}, { replace: true });
              return;
            }
            setSearchParams({ tab: value }, { replace: true });
          }}
        >
          <Tabs.List styles={{ list: { borderBottom: 'none' } }}>
            {FEATURE_VISIBILITY.caseloadAllTab ? (
              <Tabs.Tab value="all" leftSection={<IconCheck size={16} />} style={{ fontWeight: 400 }}>
                All
              </Tabs.Tab>
            ) : null}
            <Tabs.Tab value="clients" leftSection={<IconUser size={16} />} style={{ fontWeight: 400 }}>
              Clients
            </Tabs.Tab>
            <Tabs.Tab value="contacts" leftSection={<IconUsers size={16} />} style={{ fontWeight: 400 }}>
              Contacts
            </Tabs.Tab>
            {FEATURE_VISIBILITY.referralRequests ? (
              <Tabs.Tab value="referral-requests" leftSection={<IconClipboardList size={16} />} style={{ fontWeight: 400 }}>
                Referral Requests
              </Tabs.Tab>
            ) : null}
            {FEATURE_VISIBILITY.caseloadTransferPortal ? (
              <Tabs.Tab value="transfers" leftSection={<IconTransfer size={16} />} style={{ fontWeight: 400 }}>
                Transfer Portal
              </Tabs.Tab>
            ) : null}
          </Tabs.List>

          {FEATURE_VISIBILITY.caseloadAllTab ? (
            <Tabs.Panel value="all" mt="md">
              <AllTab clients={clients} setClients={setClients} clientGroups={clientGroups} onDischarge={handleDischarge} />
            </Tabs.Panel>
          ) : null}

          <Tabs.Panel value="clients" mt="md">
            <ClientsTab clients={clients} clientGroups={clientGroups} loading={caseloadLoading} />
          </Tabs.Panel>

          <Tabs.Panel value="contacts" mt="md">
            <CaseloadContactsPage
              clients={clients}
              clientGroups={clientGroups}
              caseloadLoading={caseloadLoading}
            />
          </Tabs.Panel>

          {FEATURE_VISIBILITY.caseloadTransferPortal ? (
            <Tabs.Panel value="transfers" mt="md">
              <TransfersTab refreshKey={activeTab === 'transfers' ? tabDataRefreshKey : undefined} />
            </Tabs.Panel>
          ) : null}

          {FEATURE_VISIBILITY.referralRequests ? (
            <Tabs.Panel value="referral-requests" mt="md">
              {activeTab === 'referral-requests' && (
                <ReferralsTab refreshKey={tabDataRefreshKey} />
              )}
            </Tabs.Panel>
          ) : null}
        </Tabs>

        {showAddClientModal ? (
          <StaffAddClientModal
            opened
            onClose={() => {
              setShowAddClientModal(false);
              reloadCaseload();
            }}
            practice={scopePractice}
            practices={practicesForModal}
          />
        ) : null}
      </Stack>
    </Container>
  );
}
